import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, type CanActivateFn, type RouterStateSnapshot, type UrlTree } from '@angular/router';
import { AuthSessionService } from './auth-session.service';

export const authRouteGuard: CanActivateFn = async (
  _route,
  state: RouterStateSnapshot,
): Promise<boolean | UrlTree> => {
  const authSessionService: AuthSessionService = inject(AuthSessionService);
  const router: Router = inject(Router);

  try {
    await authSessionService.ensureSession();
  } catch (error: unknown) {
    if (error instanceof HttpErrorResponse) {
      return router.createUrlTree(['/login'], { queryParams: { returnTo: state.url, authError: '1' } });
    }
    return createLoginRedirect(router, state.url);
  }

  const meContext = authSessionService.meContext();
  if (meContext !== null) {
    if (!meContext.onboardingCompleted) {
      return isOnboardingRoute(state.url) ? true : createOnboardingRedirect(router, state.url);
    }

    if (isOnboardingRoute(state.url)) {
      return router.createUrlTree(['/timeline']);
    }

    return true;
  }

  return createLoginRedirect(router, state.url);
};

function createLoginRedirect(router: Router, returnTo: string): UrlTree {
  const normalizedReturnTo: string = returnTo.trim().length > 0 ? returnTo : '/timeline';
  return router.createUrlTree(['/login'], {
    queryParams: {
      returnTo: normalizedReturnTo,
    },
  });
}

function createOnboardingRedirect(router: Router, returnTo: string): UrlTree {
  const normalizedReturnTo: string = returnTo.trim().length > 0 ? returnTo : '/timeline';
  return router.createUrlTree(['/onboarding'], {
    queryParams: {
      returnTo: normalizedReturnTo,
    },
  });
}

function isOnboardingRoute(url: string): boolean {
  const pathname: string = url.trim().split(/[?#]/, 1)[0] ?? '';
  return pathname === '/onboarding' || pathname.startsWith('/onboarding/');
}
